import { useEffect, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { MonitorSmartphone, ScanFace, FileSpreadsheet, Gavel, CheckCircle2, Delete } from "lucide-react";
import { Reveal, SectionTag, SectionTitle, Bullet } from "./Reveal";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"];
const MONO = { fontFamily: "'JetBrains Mono', ui-monospace, monospace" };

function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function FaceScanner({ reduce }) {
  return (
    <div className="relative aspect-square rounded-2xl bg-stone-900 border border-stone-800 overflow-hidden" data-testid="kiosk-face-scanner">
      <div
        className="absolute inset-0 opacity-30"
        style={{ backgroundImage: "radial-gradient(circle, #10b981 1px, transparent 1px)", backgroundSize: "18px 18px" }}
      />
      <div className="absolute inset-[18%] rounded-[38%] border border-emerald-500/40" />
      <div className={`absolute inset-[24%] rounded-[40%] border-2 border-emerald-400/70 ${reduce ? "" : "animate-pulse"}`} />
      {["top-3 left-3 border-t-2 border-l-2", "top-3 right-3 border-t-2 border-r-2", "bottom-3 left-3 border-b-2 border-l-2", "bottom-3 right-3 border-b-2 border-r-2"].map((c) => (
        <span key={c} className={`absolute h-6 w-6 border-emerald-400 rounded-sm ${c}`} />
      ))}
      {!reduce && (
        <div className="absolute inset-x-6 h-px bg-emerald-400/80 shadow-[0_0_12px_2px_rgba(16,185,129,.6)] kiosk-scanline" />
      )}
      <div className="absolute inset-x-3 bottom-3 rounded-lg bg-stone-950/85 border border-emerald-500/30 px-3 py-2 flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
        <div className="min-w-0">
          <div className="text-[11px] font-bold text-white font-body truncate">Rodrigo C. · Chapeiro líder</div>
          <div className="text-[10px] text-emerald-300 font-body">Rosto identificado · liveness ok</div>
        </div>
      </div>
    </div>
  );
}

export function KioskMockup() {
  const reduce = useReducedMotion();
  const now = useClock();
  const time = now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const date = now.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" });
  return (
    <div className="relative" data-testid="kiosk-mockup">
      <div className="absolute -inset-4 rounded-[32px] bg-emerald-500/5 blur-2xl" aria-hidden />
      <div className="relative rounded-[28px] bg-stone-950 border border-stone-800 p-3 shadow-2xl">
        <div className="rounded-[20px] bg-[#0f0e0d] border border-stone-800/80 p-6 sm:p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Ultra" className="h-7 w-7 rounded-md" />
              <span className="font-display font-bold text-white text-sm">Totem · Cozinha Matriz</span>
            </div>
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-400 font-body">
              <span className={`h-2 w-2 rounded-full bg-emerald-400 ${reduce ? "" : "animate-pulse"}`} /> Online
            </span>
          </div>

          <div className="mt-6 text-center">
            <div className="text-5xl sm:text-7xl font-black tracking-tight text-emerald-50 tabular-nums" style={MONO} data-testid="kiosk-clock">
              {time}
            </div>
            <div className="mt-1 font-body text-sm text-stone-400 capitalize">{date}</div>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-5 gap-4 items-stretch">
            <div className="sm:col-span-3 max-w-[280px] sm:max-w-none mx-auto w-full">
              <FaceScanner reduce={reduce} />
            </div>
            <div className="hidden sm:flex sm:col-span-2 flex-col">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-body font-bold mb-2">PIN de contingência</div>
              <div className="grid grid-cols-3 gap-1.5 flex-1">
                {KEYS.map((k, i) => (
                  <div
                    key={i}
                    className={`rounded-lg flex items-center justify-center text-sm font-bold font-body min-h-[34px] ${
                      k === "" ? "" : "bg-stone-900 border border-stone-800 text-stone-200"
                    }`}
                  >
                    {k === "⌫" ? <Delete className="h-3.5 w-3.5" /> : k}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-2">
            {[
              ["Turno", "07h – 15h20"],
              ["Banco de horas", "+02h15"],
              ["Almoço", "11h30"],
            ].map(([l, v]) => (
              <div key={l} className="rounded-lg bg-stone-900 border border-stone-800 px-3 py-2">
                <div className="text-[10px] text-stone-500 font-body">{l}</div>
                <div className="font-display font-bold text-white text-sm">{v}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-xl bg-emerald-500 text-stone-950 font-display font-extrabold text-center py-3.5 text-base">
            Entrada registrada · 07:02:14
          </div>
          <div className="mt-2 text-center text-[11px] text-stone-500 font-body">Comprovante enviado por e-mail e WhatsApp · Portaria 671</div>
        </div>
      </div>
      <style>{`
        @keyframes kiosk-scan { 0% { top: 12%; } 50% { top: 84%; } 100% { top: 12%; } }
        .kiosk-scanline { animation: kiosk-scan 3.2s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

export default function PillarPonto() {
  return (
    <section id="ponto" data-testid="landing-pillar-ponto" className="relative bg-stone-950 text-white py-24 overflow-hidden scroll-mt-16">
      <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-emerald-700/10 blur-3xl" aria-hidden />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-[minmax(0,1fr)] lg:grid-cols-12 gap-12 lg:gap-10 items-center">
        <Reveal className="lg:col-span-7 order-2 lg:order-1">
          <KioskMockup />
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5 order-1 lg:order-2">
          <SectionTag dark>Pilar 02 · Gestão de Pessoas</SectionTag>
          <SectionTitle dark>Totem de ponto na parede da cozinha. Zero filas, zero fraude.</SectionTitle>
          <p className="mt-5 font-body text-base md:text-lg text-stone-400 leading-relaxed">
            Transforme qualquer tablet em um relógio de ponto aderente à Portaria 671: reconhecimento facial com liveness, PIN de contingência e folha calculada automaticamente.
          </p>
          <ul className="mt-8 space-y-5">
            <Bullet dark icon={MonitorSmartphone} title="Totem kiosk em tela cheia" desc="Interface gigante pensada para dedos engordurados ou com luvas — bate ponto em 2 segundos." />
            <Bullet dark icon={ScanFace} title="Biometria facial sem contato" desc="Validação instantânea que mantém a assepsia na entrada da cozinha, com PIN de reserva." />
            <Bullet dark icon={FileSpreadsheet} title="Espelho de ponto e folha inteligente" desc="Horas extras, adicional noturno, banco de horas, faltas, DSR e atestados digitalizados em tempo real." />
            <Bullet dark icon={Gavel} title="Advertências disciplinares com IA" desc="Minutas com embasamento legal para atrasos reiterados ou descumprimento de EPI, assinadas digitalmente." />
          </ul>
        </Reveal>
      </div>
    </section>
  );
}
